import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { PermissionGuard } from "~/components/permission-guard";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { undeleteUserAccountMutation } from "~/modules/lookup/mutations";
import { activeEmailDuplicatesQuery, lookupUserByIdQuery } from "~/modules/lookup/queries";

export const Route = createFileRoute("/_dash/users/undelete-account/$userId")({
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;

    if (!params.userId) {
      throw new Error("User ID is required");
    }

    queryClient.prefetchQuery(
      lookupUserByIdQuery.queryOptions({ pathParams: { userId: params.userId } })
    );
  },
});

function RouteComponent() {
  return (
    <PermissionGuard permission="users:edit">
      <UndeleteAccountPage />
    </PermissionGuard>
  );
}

function UndeleteAccountPage() {
  const navigate = Route.useNavigate();
  const { userId } = Route.useParams();
  const [showConfirm, setShowConfirm] = useState(false);

  const { data } = lookupUserByIdQuery.useSuspenseQuery({
    pathParams: { userId },
  });

  const { data: duplicates } = activeEmailDuplicatesQuery.useQuery({
    pathParams: { userId },
  });

  const { mutate, isPending } = undeleteUserAccountMutation.useMutation({
    onSuccess() {
      toast.success("Account undeleted");
      navigate({ to: "/users/view/$userId", params: { userId } });
    },
    onError() {
      setShowConfirm(false);
      toast.error("Failed to undelete the account. Please try again.");
    },
  });

  const userLabel = data?.user?.name || data?.user?.email || userId;
  const notDeleted = !data?.user?.deletedAt;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/users/view/$userId"
          params={{ userId }}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to User
        </Link>
      </div>

      <div>
        <h1 className="text-xl font-bold">Undelete Account</h1>
      </div>

      <div className="max-w-lg space-y-6">
        <div className="rounded-md border bg-muted/20 p-4 text-sm">
          <p className="font-medium">Restore this account</p>
          <p className="mt-1 text-muted-foreground">
            This will restore the account of <span className="font-semibold">{userLabel}</span>. The
            user will regain access to their account.
          </p>
          {data?.user?.deletedAt && data.user.deletedReason && (
            <p className="mt-1 text-muted-foreground">
              Deletion reason: <span className="font-semibold">{data.user.deletedReason}</span>
            </p>
          )}
        </div>

        {duplicates && duplicates.userIds.length > 0 && (
          <div className="rounded-md border border-amber-500 bg-amber-50 p-4 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
            <p className="font-medium">Warning: email in use by other active accounts</p>
            <p className="mt-1">
              The email <span className="font-semibold">{duplicates.email}</span> is also associated
              with the following non-deleted account
              {duplicates.userIds.length > 1 ? "s" : ""}:
            </p>
            <ul className="mt-1 list-inside list-disc">
              {duplicates.userIds.map((duplicateUserId) => (
                <li key={duplicateUserId}>
                  <Link
                    to="/users/view/$userId"
                    params={{ userId: duplicateUserId }}
                    className="font-mono underline"
                  >
                    {duplicateUserId}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {notDeleted ? (
          <p className="text-sm text-muted-foreground">This account is not deleted.</p>
        ) : (
          <Button onClick={() => setShowConfirm(true)}>Undelete Account</Button>
        )}
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Account Undeletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to undelete the account of{" "}
              <span className="font-semibold">{userLabel}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => mutate({ pathParams: { userId }, params: {} })}
              disabled={isPending}
            >
              {isPending ? "Undeleting..." : "Undelete Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
